import { Stack, styled } from "~/common/mui";
import { renderCMSComponent } from "~/features/cms/componentFactory";
import type { CMSSectionComponentProps } from "~/features/cms/types/props";
import { createMarkup } from "~/features/cms/utils/markup";
import { makeExternallyStyledBaseElement, makeExternallyStyledElement } from "~/features/cms/utils/styles";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";

type HeadlineProps = {
  tag: string | undefined;
  className?: string;
  id?: string;
  content: string;
};

const RootElement = makeExternallyStyledBaseElement("div");

const BaseHeadline = styled(({ tag, id, content, className }: HeadlineProps) => {
  const Tag = (tag?.trim() ?? "p") as keyof JSX.IntrinsicElements;
  return <Tag id={id} className={className} dangerouslySetInnerHTML={createMarkup(content)} />;
})({ margin: 0 });

const StyledHeadline = makeExternallyStyledElement(
  styled(BaseHeadline)(() => ({
    ...getFontStyle(FontSize.HeadlineMedium),
    fontWeight: "bold",
  })),
);

const BaseSubheadline = styled(({ content, className }: Pick<HeadlineProps, "content" | "className">) => {
  return <p className={className} dangerouslySetInnerHTML={createMarkup(content)} />;
})(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: 0,
}));

const StyledSubheadline = makeExternallyStyledElement(BaseSubheadline);

export const CMSSection = ({
  headlineTag,
  elements,
  headline,
  subHeadline,
  htmlId,
  className,
  rootStyles,
  headlineStyles,
  subHeadlineStyles,
}: CMSSectionComponentProps) => {
  return (
    <RootElement className={className} rootStyles={rootStyles}>
      {headline && <StyledHeadline tag={headlineTag} id={htmlId} content={headline} rootStyles={headlineStyles} />}
      {subHeadline && <StyledSubheadline content={subHeadline} rootStyles={subHeadlineStyles} />}
      {elements && !!elements.length && (
        <Stack
          className={"scha__cmssection__sub-elements"}
          sx={{
            gap: 3,
          }}
        >
          {elements.map((el, index) => renderCMSComponent(el, undefined, index))}
        </Stack>
      )}
    </RootElement>
  );
};
